import ChevronDown16 from '@carbon/icons-react/es/chevron--down/16';
import ChevronRight16 from '@carbon/icons-react/es/chevron--right/16';
import { Form } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { ProjectId } from 'api/models';
import { Button } from 'components/atoms/button';
import { Panel } from 'components/atoms/collapse';
import { toastService } from 'components/molecules/toast';
import { getMembers } from 'data/members/actions';
import { useProjectDetail } from 'data/projects';
import { createAndInviteProjectMembersAction } from 'data/projects/actions';
import {
  ClickEventTags,
  useLoadLoggerForDataDog,
} from 'hooks/use-data-dog-logger';
import { useIsCustomerPortalEnabled } from 'hooks/use-feature';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { InviteUserInputV2 } from '../input-v2';
import { Footer } from './footer';
import { InvitedCustomersList } from './invited-customer-list';
import { Modal, Collapse, MemberList } from './modal.styles';

const COLLAPSE_KEY = 'team_members';

export const MembersListModal = styled(Modal)`
  width: 70rem !important;
  .ant-modal-body {
    /* width: 70rem !important; */
  }
`;

export const GlobalInviteModal = ({
  projectId,
  visible,
  handleOnClose,
  dontShowCustomerUsers,
}: {
  projectId: number;
  visible: boolean;
  handleOnClose(visibility: boolean): void;
  dontShowCustomerUsers?: boolean;
}) => {
  const dispatch = useDispatch();
  const { data: project } = useProjectDetail(ProjectId(projectId));
  const [form] = Form.useForm();
  const [collapseState, setCollapseState] = useState<string[]>([]);
  const [userFieldValMap, setUserFieldValMap] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const translate = useTranslate();

  useEffect(() => {
    dispatch(getMembers());
  }, [dispatch]);

  useLoadLoggerForDataDog([ClickEventTags.ProjectPlanInviteMemberPopup]);

  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const translatedTexts = {
    success: {
      title: useTranslation('invitation.success.title'),
      message: useTranslation('invitation.success.content'),
    },
    error: {
      title: useTranslation('invitation.team_member.error.title'),
      message: useTranslation('invitation.team_member.error.content'),
    },
  };
  const handleSuccess = useCallback(() => {
    toastService({
      toastContainer: {},
      title: translatedTexts.success.title,
      message: translatedTexts.success.message,
    }).success();
    setIsLoading(false);
    setIsSuccess(false);
  }, [translatedTexts.success.message, translatedTexts.success.title]);

  const handleFailure = useCallback(() => {
    toastService({
      toastContainer: {},
      title: translatedTexts.error.title,
      message: translatedTexts.error.message,
    }).error();
    setIsLoading(false);
    setIsSuccess(false);
  }, [translatedTexts.error.message, translatedTexts.error.title]);

  const isCpEnabled = useIsCustomerPortalEnabled();

  if (project) {
    if (isCpEnabled) {
      return isSuccess ? (
        <MembersListModal
          visible={visible}
          title={translate('project.customer_invite.fields_modal')}
          onCancel={() => handleOnClose(false)}
          footer={
            <Button
              type="primary"
              loading={isLoading}
              onClick={() => {
                setIsLoading(true);
                dispatch(
                  createAndInviteProjectMembersAction({
                    projectId: project.projectId,
                    data: Object.values(userFieldValMap),
                    handleSuccess,
                    handleFailure,
                  }),
                );
              }}
            >
              <FormattedMessage id="save" />
            </Button>
          }
        >
          <InvitedCustomersList
            projectId={projectId}
            userFieldValMap={userFieldValMap}
            setUserFieldValMap={setUserFieldValMap}
          />
        </MembersListModal>
      ) : (
        <Modal
          visible={visible}
          title={
            <FormattedMessage
              id="invite.with.label"
              values={{ label: 'people' }}
            />
          }
          onCancel={() => handleOnClose(false)}
          footer={<Footer project={project} clickHandler={handleOnClose} />}
        >
          {project ? (
            <>
              <InviteUserInputV2
                project={project}
                form={form}
                setIsSuccess={setIsSuccess}
                setUserFieldValMap={setUserFieldValMap}
                dontShowCustomerUsers={dontShowCustomerUsers}
              />
              <Collapse
                expandIconPosition="right"
                expandIcon={({ isActive }) => {
                  return isActive ? <ChevronDown16 /> : <ChevronRight16 />;
                }}
                activeKey={collapseState}
                onChange={(activeKeys) =>
                  setCollapseState(activeKeys as string[])
                }
              >
                <Panel
                  header={
                    <FormattedMessage id="project.invitation.modal.list.title" />
                  }
                  key={COLLAPSE_KEY}
                >
                  <MemberList project={project} />
                </Panel>
              </Collapse>
            </>
          ) : null}
        </Modal>
      );
    }

    return (
      <Modal
        visible={visible}
        title={
          <FormattedMessage
            id="invite.with.label"
            values={{ label: 'people' }}
          />
        }
        onCancel={() => handleOnClose(false)}
        footer={<Footer project={project} clickHandler={handleOnClose} />}
      >
        {project ? (
          <>
            <InviteUserInputV2
              project={project}
              form={form}
              setIsSuccess={setIsSuccess}
              setUserFieldValMap={setUserFieldValMap}
              dontShowCustomerUsers={dontShowCustomerUsers}
            />
            <Collapse
              expandIconPosition="right"
              expandIcon={({ isActive }) => {
                return isActive ? <ChevronDown16 /> : <ChevronRight16 />;
              }}
              activeKey={collapseState}
              onChange={(activeKeys) =>
                setCollapseState(activeKeys as string[])
              }
            >
              <Panel
                header={
                  <FormattedMessage id="project.invitation.modal.list.title" />
                }
                key={COLLAPSE_KEY}
              >
                <MemberList project={project} />
              </Panel>
            </Collapse>
          </>
        ) : null}
      </Modal>
    );
  }

  return null;
};
